/**
 * Централизованная система логирования для управления отладочными выводами
 * Позволяет включать/выключать различные категории логов
 */

interface LoggerConfig {
  // Глобальное включение/выключение всех логов
  enabled: boolean;

  // Категории логов
  categories: {
    api: boolean;
    clustering: boolean;
    keywords: boolean;
    cleaning: boolean;
    serp: boolean;
    morphology: boolean;
    filters: boolean;
    tdh: boolean;
    errors: boolean; // Всегда включено в production
  };
}

// Конфигурация по умолчанию
const defaultConfig: LoggerConfig = {
  enabled: import.meta.env.DEV, // Включено только в режиме разработки
  categories: {
    api: false,
    clustering: false,
    keywords: false,
    cleaning: false,
    serp: false,
    morphology: false,
    filters: false,
    tdh: true, // TDH включён по умолчанию для разработки
    errors: true, // Ошибки всегда логируются
  },
};

// Загрузка конфигурации из localStorage
function loadConfig(): LoggerConfig {
  try {
    const saved = localStorage.getItem("logger_config");
    if (saved) {
      return { ...defaultConfig, ...JSON.parse(saved) };
    }
  } catch (e) {
    // Игнорируем ошибки парсинга
  }
  return defaultConfig;
}

// Текущая конфигурация
let config = loadConfig();

class Logger {
  private category: keyof LoggerConfig["categories"];
  private prefix: string;

  constructor(category: keyof LoggerConfig["categories"], prefix?: string) {
    this.category = category;
    this.prefix = prefix || `[${category.toUpperCase()}]`;
  }

  private shouldLog(): boolean {
    return (
      config.enabled &&
      (config.categories[this.category] || this.category === "errors")
    );
  }

  log(...args: any[]): void {
    if (this.shouldLog()) {
      console.log(this.prefix, ...args);
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog()) {
      console.warn(this.prefix, ...args);
    }
  }

  error(...args: any[]): void {
    // Ошибки логируются всегда, если глобально включено
    if (config.enabled) {
      console.error(this.prefix, ...args);
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog()) {
      console.info(this.prefix, ...args);
    }
  }

  debug(...args: any[]): void {
    if (this.shouldLog()) {
      console.debug(this.prefix, ...args);
    }
  }

  group(label: string): void {
    if (this.shouldLog()) {
      console.group(this.prefix + " " + label);
    }
  }

  groupEnd(): void {
    if (this.shouldLog()) {
      console.groupEnd();
    }
  }
}

// Глобальные функции управления логгером
export const logger = {
  // Создание логгера для категории
  create(category: keyof LoggerConfig["categories"], prefix?: string): Logger {
    return new Logger(category, prefix);
  },

  // Включить/выключить все логи
  enable(): void {
    config.enabled = true;
    this.saveConfig();
  },

  disable(): void {
    config.enabled = false;
    this.saveConfig();
  },

  // Включить/выключить категорию
  enableCategory(category: keyof LoggerConfig["categories"]): void {
    config.categories[category] = true;
    this.saveConfig();
  },

  disableCategory(category: keyof LoggerConfig["categories"]): void {
    if (category !== "errors") {
      config.categories[category] = false;
      this.saveConfig();
    }
  },

  // Включить/выключить все категории
  enableAll(): void {
    Object.keys(config.categories).forEach((cat) => {
      config.categories[cat as keyof LoggerConfig["categories"]] = true;
    });
    this.saveConfig();
  },

  disableAll(): void {
    Object.keys(config.categories).forEach((cat) => {
      if (cat !== "errors") {
        config.categories[cat as keyof LoggerConfig["categories"]] = false;
      }
    });
    this.saveConfig();
  },

  // Получить текущую конфигурацию
  getConfig(): LoggerConfig {
    return { ...config };
  },

  // Сохранить конфигурацию
  saveConfig(): void {
    try {
      localStorage.setItem("logger_config", JSON.stringify(config));
    } catch (e) {
      // Игнорируем ошибки сохранения
    }
  },

  // Сбросить к настройкам по умолчанию
  reset(): void {
    config = defaultConfig;
    this.saveConfig();
  },
};

// Экспорт предсозданных логгеров для удобства
export const apiLogger = logger.create("api", "[API]");
export const clusteringLogger = logger.create("clustering", "[CLUSTERING]");
export const keywordsLogger = logger.create("keywords", "[KEYWORDS]");
export const cleaningLogger = logger.create("cleaning", "[CLEANING]");
export const serpLogger = logger.create("serp", "[SERP]");
export const morphologyLogger = logger.create("morphology", "[MORPHOLOGY]");
export const filtersLogger = logger.create("filters", "[FILTERS]");
export const tdhLogger = logger.create("tdh", "[TDH]");
export const errorsLogger = logger.create("errors", "[ERROR]");

// Добавляем глобальную команду для управления из консоли браузера
if (typeof window !== "undefined") {
  (window as any).semanticaLogger = {
    enable: () => logger.enable(),
    disable: () => logger.disable(),
    enableCategory: (cat: string) => logger.enableCategory(cat as any),
    disableCategory: (cat: string) => logger.disableCategory(cat as any),
    enableAll: () => logger.enableAll(),
    disableAll: () => logger.disableAll(),
    config: () => logger.getConfig(),
    reset: () => logger.reset(),
    help: () => {
      console.log(`
Semantica Logger Commands:
- semanticaLogger.enable() - enable all logs
- semanticaLogger.disable() - disable all logs
- semanticaLogger.enableAll() - enable all categories
- semanticaLogger.disableAll() - disable all categories (except errors)
- semanticaLogger.enableCategory('api') - enable a category
- semanticaLogger.disableCategory('api') - disable a category
- semanticaLogger.config() - show current configuration
- semanticaLogger.reset() - reset to defaults

Available categories: api, clustering, keywords, cleaning, serp, morphology, filters, tdh, errors
      `);
    },
  };

  console.log(
    "💡 Use semanticaLogger.help() to manage debug output",
  );
}
